import React, { useState } from "react";
import { 
  FiPlus, 
  FiUser, 
  FiMessageSquare, 
  FiCheckSquare, 
  FiAtSign, 
  FiSend,
  FiShield
} from "react-icons/fi";
import type { RcaTeamMember, RcaTeamNote } from "../../../types/rca.types.js";

export type RcaTeamCollaborationProps = {
  teamMembers?: RcaTeamMember[] | undefined;
  notes?: RcaTeamNote[] | undefined;
  onAddMember?: ((member: Partial<RcaTeamMember>) => void) | undefined;
  onPostNote?: ((msg: string) => void) | undefined;
  readOnly?: boolean | undefined;
};

const INITIAL_MEMBERS: RcaTeamMember[] = [
  { id: "tm-1", name: "John Smith", role: "RCA Owner", department: "Quality", responsibilities: "Lead Analysis & Verification", access: "Full Access", status: "Active", avatarInitials: "JS" },
  { id: "tm-2", name: "Kumar", role: "Team Lead", department: "Quality", responsibilities: "Facilitate & Review", access: "Full Access", status: "Active", avatarInitials: "KM" },
  { id: "tm-3", name: "Arun", role: "Member", department: "Production", responsibilities: "Provide Line Shift Data", access: "Edit", status: "Active", avatarInitials: "AR" },
  { id: "tm-4", name: "Priya", role: "Member", department: "Engineering", responsibilities: "Technical Tooling Analysis", access: "Edit", status: "Active", avatarInitials: "PR" },
  { id: "tm-5", name: "Ramesh", role: "Member", department: "Maintenance", responsibilities: "Machine Calibration Data", access: "View", status: "Active", avatarInitials: "RM" },
];

const INITIAL_NOTES: RcaTeamNote[] = [
  { id: "note-1", authorName: "Kumar", timestamp: "25-Aug-2026 10:30 AM", message: "Please upload latest calibration records and CMM reports for batch LOT-2026-0789.", category: "DISCUSSION" },
  { id: "note-2", authorName: "Arun", timestamp: "25-Aug-2026 11:15 AM", message: "Shift 3 maintenance check was completed. Uploaded drift log to Evidence Center.", category: "TASK" },
];

export function RcaTeamCollaboration({
  teamMembers = INITIAL_MEMBERS,
  notes = INITIAL_NOTES,
  onAddMember,
  onPostNote,
  readOnly = false,
}: RcaTeamCollaborationProps) {
  const [activeTab, setActiveTab] = useState<"MEMBERS" | "CONTRIBUTORS">("MEMBERS");
  const effectiveMembers = teamMembers && teamMembers.length > 0 ? teamMembers : INITIAL_MEMBERS;
  const [membersList, setMembersList] = useState<RcaTeamMember[]>(effectiveMembers);
  const [teamNotes, setTeamNotes] = useState<RcaTeamNote[]>(notes && notes.length > 0 ? notes : INITIAL_NOTES);
  const [newNoteInput, setNewNoteInput] = useState<string>("");
  const [showMemberModal, setShowMemberModal] = useState<boolean>(false);
  const [newMemberName, setNewMemberName] = useState<string>("");
  const [newMemberRole, setNewMemberRole] = useState<RcaTeamMember["role"]>("Member");
  const [newMemberDept, setNewMemberDept] = useState<string>("Quality");
  const [newMemberResp, setNewMemberResp] = useState<string>("Contribute to root cause analysis");

  React.useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      setMembersList(teamMembers);
    }
  }, [teamMembers]);

  React.useEffect(() => {
    if (notes && notes.length > 0) {
      setTeamNotes(notes);
    }
  }, [notes]);

  const handleSendNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteInput.trim() || readOnly) return;
    const newNote: RcaTeamNote = {
      id: `note-${Date.now()}`,
      authorName: "John Smith (RCA Owner)",
      timestamp: "Just now",
      message: newNoteInput.trim(),
      category: "DISCUSSION",
    };
    setTeamNotes([...teamNotes, newNote]);
    setNewNoteInput("");
    onPostNote?.(newNote.message);
  };

  const handleAddMemberSubmit = () => {
    if (!newMemberName.trim() || readOnly) return;
    const initials = newMemberName
      .split(" ")
      .map(w => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    const newMember: RcaTeamMember = {
      id: `tm-${Date.now()}`,
      name: newMemberName.trim(),
      role: newMemberRole,
      department: newMemberDept,
      responsibilities: newMemberResp,
      access: "Edit",
      status: "Active",
      avatarInitials: initials || "TM",
    };
    setMembersList([...membersList, newMember]);
    onAddMember?.(newMember);
    setShowMemberModal(false);
    setNewMemberName("");
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; Investigation Team</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">7. Team & Collaboration</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Cross-functional investigation team members, roles, responsibilities, and team notes.
          </p>
        </div>

        {!readOnly && (
          <button
            type="button"
            onClick={() => setShowMemberModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-all"
          >
            <FiPlus className="w-4 h-4" />
            Add Member
          </button>
        )}
      </div>

      {/* Main Grid: Left Team Members Table + Right Team Chat/Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Left 2 Cols: Members List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-3">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900">Assigned Team Members ({membersList.length})</h3>
            <span className="text-xs text-slate-500 font-semibold">{membersList.filter(m => m.status === "Active").length} Active</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 uppercase text-[11px]">
                <tr>
                  <th className="py-3 px-4">Name & Avatar</th>
                  <th className="py-3 px-3">Role</th>
                  <th className="py-3 px-3">Department</th>
                  <th className="py-3 px-3">Responsibilities</th>
                  <th className="py-3 px-3">Access</th>
                  <th className="py-3 px-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {membersList.map(member => (
                  <tr key={member.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3.5 px-4 font-bold text-slate-900 flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-bold text-[11px]">
                        {member.avatarInitials || member.name.slice(0, 2).toUpperCase()}
                      </div>
                      <span>{member.name}</span>
                    </td>
                    <td className="py-3.5 px-3 font-semibold text-blue-600">{member.role}</td>
                    <td className="py-3.5 px-3 text-slate-600">{member.department}</td>
                    <td className="py-3.5 px-3 text-slate-700 max-w-[200px] truncate">{member.responsibilities}</td>
                    <td className="py-3.5 px-3 text-slate-600 font-medium">{member.access}</td>
                    <td className="py-3.5 px-3 text-right">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {member.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right 1 Col: Team Notes Feed */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4 flex flex-col justify-between h-[450px]">
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <FiMessageSquare className="w-4 h-4 text-blue-600" />
              Team Activity & Notes
            </h3>

            <div className="space-y-3 overflow-y-auto max-h-[300px] pr-1">
              {teamNotes.map(note => (
                <div key={note.id} className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-bold text-blue-600">{note.authorName}</span>
                    <span className="text-slate-400">{note.timestamp}</span>
                  </div>
                  <p className="text-slate-800 font-medium leading-relaxed">{note.message}</p>
                </div>
              ))}
            </div>
          </div>

          {/* New Note Form */}
          <form onSubmit={handleSendNote} className="pt-2 border-t border-slate-100 flex gap-2">
            <input
              type="text"
              value={newNoteInput}
              disabled={readOnly}
              onChange={(e) => setNewNoteInput(e.target.value)}
              placeholder="Add investigation note or mention..."
              className="flex-1 text-xs p-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              disabled={readOnly || !newNoteInput.trim()}
              className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold shadow-sm flex items-center"
            >
              <FiSend className="w-3.5 h-3.5" />
            </button>
          </form>
        </div>
      </div>

      {/* Add Member Modal */}
      {showMemberModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-md w-full p-6 space-y-4">
            <h3 className="text-base font-bold text-slate-900">Add Team Member</h3>
            <p className="text-xs text-slate-500">
              Assign a cross-functional team member to this RCA investigation.
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Full Name:</label>
                <input
                  type="text"
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  placeholder="e.g. Anand Sharma"
                  className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Role:</label>
                  <select
                    value={newMemberRole}
                    onChange={(e) => setNewMemberRole(e.target.value as RcaTeamMember["role"])}
                    className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                  >
                    <option value="RCA Owner">RCA Owner</option>
                    <option value="Team Lead">Team Lead</option>
                    <option value="Member">Member</option>
                    <option value="Contributor">Contributor</option>
                    <option value="Reviewer">Reviewer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Department:</label>
                  <select
                    value={newMemberDept}
                    onChange={(e) => setNewMemberDept(e.target.value)}
                    className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                  >
                    <option value="Quality">Quality</option>
                    <option value="Production">Production</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Supply Chain">Supply Chain</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Responsibilities:</label>
                <input
                  type="text"
                  value={newMemberResp}
                  onChange={(e) => setNewMemberResp(e.target.value)}
                  placeholder="e.g. CNC axis calibration data & maintenance log"
                  className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowMemberModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!newMemberName.trim()}
                onClick={handleAddMemberSubmit}
                className={`px-4 py-2 rounded-lg text-xs font-semibold shadow-sm transition-all ${
                  newMemberName.trim() 
                    ? "bg-blue-600 hover:bg-blue-700 text-white" 
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                }`}
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
